import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp, type AppState } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import {
  CapacitorUpdater,
  type BundleInfo,
  type LatestVersion,
} from "@capgo/capacitor-updater";
import * as Sentry from "@sentry/react";
import { setOtaApplying } from "../utils/otaState";
import { isCompatible } from "../utils/semver";
import { ENABLE_OTA_LOGS } from "../constants/logging";

const DOWNLOAD_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const BACKGROUND_APPLY_MS = 10 * 60 * 1000; // 10 minutes in background before applying
const OTA_ENABLED = import.meta.env.VITE_ENABLE_OTA === "true";
const PENDING_BUNDLE_KEY = "ota_pending_bundle_id";
const FIRST_RUN_KEY = "first_run_ota_done";
const BACKGROUND_TS_KEY = "ota_background_ts";
const LAST_NATIVE_VERSION_KEY = "ota_last_native_version";

// TESTING ONLY — leave in `false` in every production build.
// When `true`, Phase 1 skips auto-applying a pending bundle at launch. Polling
// and background apply (Phase 2) keep working. Flip to `true` locally when
// you need to install a new build via Xcode without the app immediately
// applying a pending OTA and blowing away the test state you were holding.
// No runtime flag on purpose: rebuild to change, to avoid a prod user (or a
// leaked Safari Inspector path) from being able to pause updates.
const DISABLE_LAUNCH_APPLY = false;

// Per-stage timeouts for the first-run OTA flow. getLatest is just a small
// metadata HTTP check and should be near-instant; download can be MB-sized
// and needs more headroom. Either timeout falls through to the builtin path
// without losing the next-launch retry (markFirstRunDone is still called).
const FIRST_RUN_GET_LATEST_TIMEOUT_MS = 5000;
const FIRST_RUN_DOWNLOAD_TIMEOUT_MS = 20000;

const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  label: string,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(`Timeout(${timeoutMs}ms): ${label}`)),
      timeoutMs,
    );
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const log = (...args: unknown[]) => {
  if (!ENABLE_OTA_LOGS) return;
  // eslint-disable-next-line no-console
  console.log(...args);
};
const warn = (...args: unknown[]) => {
  if (!ENABLE_OTA_LOGS) return;
  // eslint-disable-next-line no-console
  console.warn(...args);
};

type Listener = { remove: () => void } | undefined;

const setPendingBundleId = async (bundleId: string) => {
  await Preferences.set({ key: PENDING_BUNDLE_KEY, value: bundleId });
};

const getPendingBundleId = async () => {
  const { value } = await Preferences.get({ key: PENDING_BUNDLE_KEY });
  return value ?? null;
};

const clearPendingBundleId = async () => {
  await Preferences.remove({ key: PENDING_BUNDLE_KEY });
};

const markFirstRunDone = async () => {
  await Preferences.set({ key: FIRST_RUN_KEY, value: "true" });
};

const setBackgroundTimestamp = async (timestamp: number) => {
  await Preferences.set({ key: BACKGROUND_TS_KEY, value: String(timestamp) });
};

const getBackgroundTimestamp = async () => {
  const { value } = await Preferences.get({ key: BACKGROUND_TS_KEY });
  return value ? Number(value) : null;
};

const clearBackgroundTimestamp = async () => {
  await Preferences.remove({ key: BACKGROUND_TS_KEY });
};

const getNativeVersion = async (): Promise<string | null> => {
  try {
    const builtin = await CapacitorUpdater.getBuiltinVersion();
    return builtin?.version ?? null;
  } catch (error) {
    warn("[OTA] getBuiltinVersion failed", error);
    return null;
  }
};

// Find the version string of a downloaded bundle by id. Used to verify
// compatibility before applying a pending bundle — we only have its id in
// Preferences, not its version, so we cross-reference the local bundle list.
const getBundleVersionById = async (id: string): Promise<string | null> => {
  try {
    const list = await CapacitorUpdater.list();
    const match = list.bundles?.find((b) => b.id === id);
    return match?.version ?? null;
  } catch (error) {
    warn("[OTA] list bundles failed", error);
    return null;
  }
};

// Reports a rejected OTA to Sentry so ops can see when channels have bundles
// incompatible with the user's native. Level=warning to avoid paging, kept
// tagged so they're searchable as a group.
const reportDriftRejection = (
  stage: "first-run" | "download" | "apply",
  nativeVersion: string | null,
  otaVersion: string | null,
  extra: Record<string, unknown> = {},
) => {
  try {
    Sentry.captureMessage(
      `[OTA] rejected ${stage} — major/minor mismatch (native=${nativeVersion ?? "?"}, ota=${otaVersion ?? "?"})`,
      {
        level: "warning",
        tags: { ota_drift: stage, ota_native: nativeVersion ?? "unknown" },
        extra: { nativeVersion, otaVersion, ...extra },
      },
    );
  } catch {
    // Sentry errors shouldn't break OTA flow.
  }
};

export const useOtaUpdates = () => {
  const [otaReady, setOtaReady] = useState(
    !OTA_ENABLED || !Capacitor.isNativePlatform(),
  );
  const downloadInFlightRef = useRef(false);
  const applyInFlightRef = useRef(false);
  const backgroundTimestampRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const foregroundHandlingRef = useRef(false);

  const neutralizeNextBundle = async (origin: string) => {
    try {
      const current = await CapacitorUpdater.current();
      const currentId = current?.bundle?.id;
      if (!currentId) return;
      await CapacitorUpdater.next({ id: currentId });
      log("[OTA] next cleared to current bundle", {
        origin,
        current: currentId,
      });
      try {
        await CapacitorUpdater.notifyAppReady();
        log("[OTA] appReady reaffirmed after clearing next", {
          origin,
          current: currentId,
        });
      } catch (error) {
        warn("[OTA] notifyAppReady failed after clearing next", {
          origin,
          error,
        });
      }
    } catch (error) {
      warn("[OTA] failed to clear next bundle", { origin, error });
    }
  };

  // Phase 1: at launch, apply pending bundle BEFORE rendering the rest of the app to avoid double biometric flows.
  useEffect(() => {
    if (!OTA_ENABLED || !Capacitor.isNativePlatform()) {
      setOtaReady(true);
      return;
    }

    let cancelled = false;

    const maybeApplyPendingOnLaunch = async () => {
      // Defensive check: if the native binary changed since the last run
      // (e.g. App Store update), force every piece of OTA state back to a
      // clean slate. Normally Capgo's own cleanupObsoleteVersions() handles
      // this via CFBundleVersion, but if the team ever ships again with a
      // stale buildNumber (the bug this fix is cleaning up after), this
      // check catches it from the JS side. Also happens to subsume the
      // "FIRST_RUN_KEY versioned per native" idea — clearing the key makes
      // the eager download path re-run on the new native.
      const currentNative = await getNativeVersion();
      try {
        const { value: lastNative } = await Preferences.get({
          key: LAST_NATIVE_VERSION_KEY,
        });
        if (currentNative && lastNative && lastNative !== currentNative) {
          warn("[OTA] native changed since last run, forcing reset", {
            from: lastNative,
            to: currentNative,
          });
          try {
            await CapacitorUpdater.reset({ toLastSuccessful: false });
          } catch (resetError) {
            warn("[OTA] native-change reset failed", resetError);
          }
          await clearPendingBundleId();
          await clearBackgroundTimestamp();
          await Preferences.remove({ key: FIRST_RUN_KEY });
        }
        if (currentNative) {
          await Preferences.set({
            key: LAST_NATIVE_VERSION_KEY,
            value: currentNative,
          });
        }
      } catch (nativeCheckError) {
        warn("[OTA] native-version check failed, continuing", nativeCheckError);
      }

      // Testing escape hatch. Rebuild with DISABLE_LAUNCH_APPLY=true to stop
      // Phase 1 from touching pending/first-run logic while QA holds a
      // specific device state across Xcode installs.
      if (DISABLE_LAUNCH_APPLY) {
        warn(
          "[OTA] DISABLE_LAUNCH_APPLY=true — skipping launch apply (testing)",
        );
        await neutralizeNextBundle("launch-disabled");
        if (!cancelled) setOtaReady(true);
        return;
      }

      try {
        const pending = await getPendingBundleId();
        if (pending) {
          log("[OTA] launch: pending bundle found", pending);
          try {
            const current = await CapacitorUpdater.current();
            if (current?.bundle?.id === pending) {
              log("[OTA] launch: pending already current, clearing", {
                current: current.bundle.id,
              });
              await clearPendingBundleId();
              if (!cancelled) {
                setOtaReady(true);
              }
              return;
            }
          } catch (error) {
            warn("[OTA] failed to read current bundle", error);
          }

          // Drift check before applying a pending bundle. Only patches (same
          // major.minor) are allowed — a minor bump means the native binary
          // changed in ways the user's install may not have, so we drop the
          // pending and continue boot on builtin.
          const pendingVersion = await getBundleVersionById(pending);
          if (
            pendingVersion &&
            currentNative &&
            !isCompatible(pendingVersion, currentNative)
          ) {
            warn("[OTA] launch: pending bundle version mismatch, discarding", {
              pendingVersion,
              currentNative,
            });
            reportDriftRejection("apply", currentNative, pendingVersion, {
              stage: "launch",
              pendingId: pending,
            });
            await clearPendingBundleId();
            // Fall through to neutralize + otaReady.
          } else {
            await clearPendingBundleId(); // avoid loops if set() fails
            // Do not set otaReady; this context will be killed by set()
            log("[OTA] launch: applying pending bundle", pending);
            await CapacitorUpdater.set({ id: pending });
            return;
          }
        }

        const { value: firstRunFlag } = await Preferences.get({
          key: FIRST_RUN_KEY,
        });
        const isFirstRun = firstRunFlag !== "true";

        if (isFirstRun) {
          log("[OTA] launch: first run detected");
          try {
            const latest: LatestVersion | undefined = await withTimeout(
              CapacitorUpdater.getLatest(),
              FIRST_RUN_GET_LATEST_TIMEOUT_MS,
              "first-run getLatest",
            );

            const hasUpdate =
              latest &&
              latest.error !== "no_new_version_available" &&
              latest.url &&
              latest.version;

            if (hasUpdate) {
              // Drift check: never apply a first-run bundle whose
              // major.minor differs from the native. Mark first-run done so
              // we don't keep retrying this path on every boot.
              if (
                currentNative &&
                !isCompatible(latest.version, currentNative)
              ) {
                warn(
                  "[OTA] launch: first-run bundle version mismatch, skipping",
                  { otaVersion: latest.version, currentNative },
                );
                reportDriftRejection(
                  "first-run",
                  currentNative,
                  latest.version ?? null,
                );
                await markFirstRunDone();
              } else {
                log("[OTA] launch: update available", {
                  version: latest.version,
                });
                const bundle: BundleInfo = await withTimeout(
                  CapacitorUpdater.download({
                    url: latest.url as string,
                    version: latest.version as string,
                    checksum: latest.checksum,
                    sessionKey: latest.sessionKey,
                    manifest: latest.manifest,
                  }),
                  FIRST_RUN_DOWNLOAD_TIMEOUT_MS,
                  "first-run download",
                );

                log("[OTA] launch: downloaded bundle", bundle.id);
                await markFirstRunDone();
                await clearPendingBundleId();
                log("[OTA] launch: applying first-run bundle", bundle.id);
                await CapacitorUpdater.set({ id: bundle.id });
                return;
              }
            } else {
              log("[OTA] launch: no update available");
              await markFirstRunDone();
            }
          } catch (firstRunError) {
            // `getLatest()` on Android throws rather than returning with
            // `error: "no_new_version_available"` (which the hasUpdate check
            // above handles). Treat the thrown case the same — it's a normal
            // "nothing to do", not a real failure — to avoid a misleading
            // warn log on every first boot of an already-up-to-date install.
            const message =
              (firstRunError as { message?: string })?.message ??
              String(firstRunError);
            const isTimeout = message.startsWith("Timeout(");
            const stage = message.includes("first-run getLatest")
              ? "getLatest"
              : message.includes("first-run download")
                ? "download"
                : "other";

            if (message.includes("no_new_version_available")) {
              log("[OTA] launch: no update available");
            } else {
              warn("[OTA] firstRun: aborted — proceeding with builtin", {
                isTimeout,
                stage,
                message,
              });
              try {
                Sentry.captureMessage(
                  `[OTA] first-run aborted — ${isTimeout ? "timeout" : "error"} at ${stage}`,
                  {
                    level: "warning",
                    tags: {
                      ota_first_run_abort: isTimeout ? "timeout" : "error",
                      ota_first_run_stage: stage,
                    },
                    extra: {
                      message,
                      getLatestTimeoutMs: FIRST_RUN_GET_LATEST_TIMEOUT_MS,
                      downloadTimeoutMs: FIRST_RUN_DOWNLOAD_TIMEOUT_MS,
                    },
                  },
                );
              } catch {
                // Sentry should never block the OTA boot.
              }
            }
            // Critical: mark first-run done even on timeout/error so the next
            // launch does not repeat the same hang. Phase 2 polling will
            // fetch updates later (every 3 minutes after otaReady).
            try {
              await markFirstRunDone();
            } catch (markError) {
              warn(
                "[OTA] firstRun: markFirstRunDone failed after abort",
                markError,
              );
            }
          }
        }
      } catch (error) {
        warn("[OTA] pending apply on launch failed, continuing", error);
      }
      await neutralizeNextBundle("launch");
      if (!cancelled) {
        log("[OTA] launch: otaReady");
        setOtaReady(true);
      }
    };

    void maybeApplyPendingOnLaunch();

    return () => {
      cancelled = true;
    };
  }, []);

  // Phase 2: once ready (no pending apply), manage downloads + background apply timer.
  useEffect(() => {
    if (!otaReady) return;
    if (!OTA_ENABLED) return;
    if (!Capacitor.isNativePlatform()) return;

    let resumeListener: Listener;
    let appStateListener: Listener;
    // Guards against the effect being cleaned up while the async IIFE below
    // is still resolving addListener. Mirrors the pattern already used in
    // Phase 1 so listeners registered post-cleanup still get removed.
    let cancelled = false;

    const applyPendingNow = async (origin: string) => {
      if (applyInFlightRef.current) return false;
      applyInFlightRef.current = true;
      try {
        const pending = await getPendingBundleId();
        if (!pending) return false;

        const current = await CapacitorUpdater.current();
        if (current?.bundle?.id === pending) {
          log("[OTA] apply: pending already current, clearing", {
            origin,
            pending,
          });
          await clearPendingBundleId();
          return false;
        }

        // Drift check before the set(). If somehow we got here with a pending
        // bundle whose major.minor differs from native (e.g. channel config
        // drift, manual pending injection), discard it rather than reload
        // into a broken webview.
        const currentNative = await getNativeVersion();
        const pendingVersion = await getBundleVersionById(pending);
        if (
          pendingVersion &&
          currentNative &&
          !isCompatible(pendingVersion, currentNative)
        ) {
          warn("[OTA] apply: pending bundle version mismatch, discarding", {
            origin,
            pending,
            pendingVersion,
            currentNative,
          });
          reportDriftRejection("apply", currentNative, pendingVersion, {
            origin,
            pendingId: pending,
          });
          await clearPendingBundleId();
          return false;
        }

        await clearPendingBundleId();
        await clearBackgroundTimestamp();
        log("[OTA] apply: setting bundle", { origin, pending });
        await CapacitorUpdater.set({ id: pending });
        return true;
      } catch (error) {
        warn("[OTA] apply pending failed", { origin, error });
        return false;
      } finally {
        applyInFlightRef.current = false;
      }
    };

    const downloadLatest = async (origin: string) => {
      if (downloadInFlightRef.current) return;
      downloadInFlightRef.current = true;
      try {
        log("[OTA] check latest", { origin });
        const existingPending = await getPendingBundleId();
        if (existingPending) {
          log("[OTA] skip download, pending bundle exists", {
            origin,
            pending: existingPending,
          });
          return;
        }
        const latest: LatestVersion | undefined =
          await CapacitorUpdater.getLatest();

        if (
          !latest ||
          latest.error === "no_new_version_available" ||
          !latest.url ||
          !latest.version
        ) {
          log("[OTA] no new version", { origin });
          return;
        }

        const bundle: BundleInfo = await CapacitorUpdater.download({
          url: latest.url as string,
          version: latest.version as string,
          checksum: latest.checksum,
          sessionKey: latest.sessionKey,
          manifest: latest.manifest,
        });

        log("[OTA] download complete", {
          origin,
          id: bundle.id,
          version: bundle.version,
        });

        // Drift check: if the just-downloaded bundle has a different
        // major.minor than native, do NOT mark it as pending. Leaving it
        // unpending means neither Phase 1 nor the background-apply timer
        // will try to use it. The bundle stays on disk (Capgo's cleanup
        // handles eventually), and the next poll retries.
        const currentNative = await getNativeVersion();
        if (currentNative && !isCompatible(bundle.version, currentNative)) {
          warn("[OTA] download: version mismatch, not pending", {
            origin,
            bundleVersion: bundle.version,
            currentNative,
          });
          reportDriftRejection("download", currentNative, bundle.version, {
            origin,
            bundleId: bundle.id,
          });
          return;
        }

        await setPendingBundleId(bundle.id);
        await neutralizeNextBundle(origin);
      } catch (error: unknown) {
        const message =
          (error as { message?: string })?.message ?? String(error);
        if (!message?.includes("no_new_version_available")) {
          warn("[OTA] download/check failed", { origin, message }, error);
        }
      } finally {
        downloadInFlightRef.current = false;
      }
    };

    // Kick off once on mount (cold start path downloads)
    void downloadLatest("launch");

    // Periodic checks while app is running
    intervalRef.current = window.setInterval(
      () => void downloadLatest("interval"),
      DOWNLOAD_INTERVAL_MS,
    );

    const handleForeground = async (origin: string) => {
      if (foregroundHandlingRef.current) {
        log("[OTA] foreground: skip, already handling", { origin });
        return;
      }
      foregroundHandlingRef.current = true;
      try {
        let applied = false;
        const backgroundedAt =
          backgroundTimestampRef.current ?? (await getBackgroundTimestamp());

        if (backgroundedAt) {
          const delta = Date.now() - backgroundedAt;
          log("[OTA] foreground delta", { origin, delta });
          if (delta >= BACKGROUND_APPLY_MS) {
            // Only block biometric flows if there is actually a pending bundle to apply.
            // Previously, setOtaApplying(true) was called before checking, which caused
            // the resume biometric reauth to be skipped even when there was nothing to apply.
            const pending = await getPendingBundleId();
            if (pending) {
              setOtaApplying(true);
              setOtaReady(false);

              applied = await applyPendingNow(origin);
              if (applied) {
                // CapacitorUpdater.set() reloads the app; if it silently fails,
                // restore state so the app doesn't stay stuck on the loading spinner.
                setOtaApplying(false);
                setOtaReady(true);
                return;
              }

              // Bundle was pending but apply failed — restore normal state.
              setOtaApplying(false);
              setOtaReady(true);
            }
          }
        }

        backgroundTimestampRef.current = null;
        await clearBackgroundTimestamp();
        void downloadLatest(origin);
      } finally {
        // try/finally guarantees the handling ref is always released, even if
        // any await above throws. Without it a single Preferences/plugin error
        // would zombify the hook until a cold boot.
        foregroundHandlingRef.current = false;
      }
    };

    // Foreground check after backgrounding
    void (async () => {
      resumeListener = await CapApp.addListener(
        "resume",
        () => void handleForeground("resume"),
      );
      if (cancelled) {
        resumeListener?.remove?.();
        resumeListener = undefined;
        return;
      }

      appStateListener = await CapApp.addListener(
        "appStateChange",
        ({ isActive }: AppState) => {
          if (isActive) {
            void handleForeground("appStateChange");
          } else {
            const now = Date.now();
            log("[OTA] background timestamp set", now);
            backgroundTimestampRef.current = now;
            void setBackgroundTimestamp(now);
          }
        },
      );
      if (cancelled) {
        appStateListener?.remove?.();
        appStateListener = undefined;
      }
    })();

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      resumeListener?.remove?.();
      appStateListener?.remove?.();
    };
  }, [otaReady]);

  return { otaReady };
};
